<template>
  <div class="org-object-actions-menu">
    <MenuIcon
      :dropdownItems="visibleMenuItems"
      :isOpen="isMenuOpen"
      :menuWidth="true"
      @onClick="handleMenuClick"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import MenuIcon from '@components/MenuIcon/MenuIcon.vue';
import { getOrgObjectActions, isValidObjectType } from './utils/statusGating.js';

const props = defineProps({
  /**
   * The org object type (one of the six objects sharing the canonical lifecycle).
   */
  objectType: {
    type: String,
    required: true,
    validator: (value) => isValidObjectType(value),
  },
  /**
   * Current status of the org object.
   */
  status: {
    type: String,
    required: true,
    validator: (s) => ['Draft', 'Active', 'Retired', 'Archived'].includes(s),
  },
  /**
   * Optional object-specific actions to append after the standard lifecycle actions.
   * Each action should match MenuIcon's shape: {name, value, icon, destructive?, negative?}
   */
  objectSpecificActions: {
    type: Array,
    default: () => [],
  },
  /**
   * Controls external menu open state (for MenuIcon's isOpen prop).
   */
  menuOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['action']);

const isMenuOpen = ref(props.menuOpen);

watch(
  () => props.menuOpen,
  (newVal) => {
    isMenuOpen.value = newVal;
  }
);

const visibleMenuItems = computed(() => {
  return getOrgObjectActions({
    objectType: props.objectType,
    status: props.status,
    objectSpecificActions: props.objectSpecificActions,
  });
});

function handleMenuClick(item) {
  isMenuOpen.value = false;
  emit('action', item.value);
}

defineExpose({ handleMenuClick, visibleMenuItems });
</script>

<style scoped>
.org-object-actions-menu {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
